import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

/** Only these variants map to actual HTML elements */
type Variant = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "span" | "p" | "label";

type StatusType = "ASSIGNED" | "DRAFT" | "FAILED" | "PENDING";

type GradientDirection = "r" | "l" | "t" | "b" | "tr" | "tl" | "br" | "bl";

type ElementType = {
  [K in Variant]: React.ElementRef<K>;
};

const textVariants = cva("", {
  /**
   * Text variant styles configuration using class-variance-authority
   * @property variant - Different text styles for headings and text
   */
  variants: {
    variant: {
      heroTitle: "text-4xl font-extrabold",
      h1: "text-2xl font-bold",
      h2: "text-xl font-semibold",
      h3: "text-lg font-semibold",
      h4: "text-base font-medium",
      h5: "text-base",
      h6: "text-base",
      span: "text-base",
      p: "text-base",
      label: "text-sm font-medium",
    },
    /**
     * Text variant styles configuration using class-variance-authority
     * @property size - Text size variants from sm to 4xl
     */
    size: {
      sm: "text-sm",
      base: "text-base",
      lg: "text-lg",
      xl: "text-xl",
      "2xl": "text-2xl",
      "3xl": "text-3xl",
      "4xl": "text-4xl",
    },
    /**
     * Text variant styles configuration using class-variance-authority
     * @property weight - Font weight variants
     */
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
      extrabold: "font-extrabold",
    },
  },
});

const getStatusStyles = (status: StatusType) => {
  switch (status) {
    case "ASSIGNED":
      return "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-100";
    case "DRAFT":
      return "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100";
    case "FAILED":
      return "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-100";
    default:
      return "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-100";
  }
};

const getGradientClasses = (
  direction: GradientDirection = "r",
  from?: string,
  via?: string,
  to?: string,
) => {
  if (!from || !to) return "";

  return twMerge(
    "bg-clip-text",
    "text-transparent",
    `bg-gradient-to-${direction}`,
    from,
    via,
    to,
    `dark:bg-gradient-to-${direction}`,
  );
};

/**
 * Rich text component supporting gradients, dark mode, translations, and status styling.
 *
 * @example
 * ```tsx
 * // Gradient heading with dark mode support
 * <TextLinear
 *   variant="h1"
 *   from="blue-500:blue-400"
 *   to="purple-500:purple-400"
 * >
 *   Hello World
 * </TextLinear>
 *
 * // Translated text with status
 * <TextLinear status="ASSIGNED" values={{ name: "John" }}>
 *   welcome.message
 * </TextLinear>
 * ```
 */
interface TextLinearProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "children" | "translate">,
    VariantProps<typeof textVariants> {
  /** Replace the underlying element with a custom one using Radix Slot */
  asChild?: boolean;

  /** Content to display. If string, used as translation key */
  children: React.ReactNode;

  /** Values to interpolate into translated text (e.g. { name: "John" }) */
  values?: Record<string, string | number | undefined>;

  /** Apply status-based styling with predefined color schemes */
  status?: StatusType;

  /** Starting gradient color with optional dark mode variant (e.g. "blue-500:blue-400") */
  from?: string;

  /** Middle gradient color with optional dark mode variant format: "color:darkColor" */
  via?: string;

  /** Ending gradient color with optional dark mode variant format: "color:darkColor" */
  to?: string;

  /** Direction of the gradient effect */
  direction?: GradientDirection;
}

/**
 * Gets the appropriate HTML element type based on the variant
 * @param variant - The text variant to get element type for
 * @returns The corresponding HTML element type
 */
const getElementType = (
  variant: VariantProps<typeof textVariants>["variant"],
) => {
  if (variant === "heroTitle") return "h1";
  return (variant || "span") as keyof JSX.IntrinsicElements;
};

/**
 * Text component with gradient, dark mode, and i18n support
 *
 * @example
 * ```tsx
 * <TextLinear
 *   variant="h1"
 *   from="blue-500:blue-400"
 *   to="purple-500:purple-400"
 *   direction="r"
 * >
 *   Gradient Heading
 * </TextLinear>
 * ```
 */
const TextLinear = React.forwardRef<ElementType[Variant], TextLinearProps>(
  (
    {
      className,
      variant,
      size,
      weight,
      asChild = false,
      children,
      values,
      status,
      from,
      via,
      to,
      direction = "r",
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const Component = asChild ? Slot : getElementType(variant);

    const processedValues = React.useMemo(() => {
      if (!values) return undefined;

      return Object.fromEntries(
        Object.entries(values).map(([key, value]) => [
          key,
          value === undefined ? "" : value,
        ]),
      );
    }, [values]);

    const content = React.useMemo(() => {
      if (typeof children !== "string") return children;

      try {
        return t(children, processedValues);
      } catch (error) {
        console.warn("Translation failed:", error);
        return children;
      }
    }, [children, t, processedValues]);

    const combinedClassName = React.useMemo(() => {
      const baseClasses = textVariants({ variant, size, weight });
      const gradientClasses = from
        ? getGradientClasses(direction, from, via, to)
        : "";
      const statusClasses = status ? getStatusStyles(status) : "";

      return twMerge(baseClasses, gradientClasses, statusClasses, className);
    }, [variant, size, weight, className, status, direction, from, via, to]);

    return React.createElement(Component, {
      className: combinedClassName,
      ref: ref as never,
      ...props,
      children: content,
    });
  },
);

TextLinear.displayName = "TextLinear";

export { TextLinear, textVariants };
